/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import PropTypes from "prop-types";
import React from "react";
import PhotoStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/PhotoStoryTemplates";
import LiveBlogStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/LiveBlogStoryTemplates";
import TextStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/TextStoryTemplates";
import ListicleStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/ListicleStoryTemplates";
import VideoStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/VideoStoryTemplates";
import { StoryFooter } from "../molecules/story-footer";
import { UpdateOnInterval } from "@quintype/components";
import get from "lodash/get";
import { getStoryDataWithAdSlots } from "./get-story";

const wrapper = StoryComponent => ({
  story,
  templateConfig,
  preview,
  storyElementsConfig,
  adComponent,
  widgetComp
}) => {
  return (
    <StoryComponent
      story={story}
      config={templateConfig}
      storyElementsConfig={storyElementsConfig}
      adComponent={adComponent}
      widgetComp={widgetComp}
    >
      {!preview && <StoryFooter story={story} />}
    </StoryComponent>
  );
};

const StoryTemplate = ({
  subPageType,
  story,
  templateConfig,
  preview,
  storyElementsConfig,
  adComponent,
  widgetComp
}) => {
  switch (subPageType) {
    case "video":
      return wrapper(VideoStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp
      });
    case "photo":
      return wrapper(PhotoStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp
      });
    case "listicle":
      return wrapper(ListicleStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp
      });
    case "live-blog":
      return wrapper(LiveBlogStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp
      });
    case "text":
    default:
      return wrapper(TextStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp
      });
  }
};

const PublicStory = ({
  story,
  templateConfig,
  preview,
  storyElementsConfig,
  adComponent,
  widgetComp,
  storyBaseType
}) => {
  const subPageType = storyBaseType || story["story-template"];
  const storyId = get(story, ["id"], "");
  const { storySlots = [] } = templateConfig;

  const storyDataLoader = () => getStoryDataWithAdSlots(storyId, storySlots);

  if (subPageType === "live-blog") {
    return (
      <UpdateOnInterval dataLoader={storyDataLoader} interval={20000} initData={{ story: story }}>
        {({ data }) => {
          const { story } = data;
          return StoryTemplate({
            subPageType,
            story,
            templateConfig,
            preview,
            storyElementsConfig,
            data,
            adComponent,
            widgetComp
          });
        }}
      </UpdateOnInterval>
    );
  } else {
    return StoryTemplate({ subPageType, story, templateConfig, preview, storyElementsConfig, adComponent, widgetComp });
  }
};

PublicStory.propTypes = {
  story: PropTypes.object,
  templateConfig: PropTypes.object,
  preview: PropTypes.bool,
  storyElementsConfig: PropTypes.object,
  storyBaseType: PropTypes.string
};

export default PublicStory;
